import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import {
  appendImageURL,
  checkIfPastDate,
  convertTimestampToDate,
  convertTimeToLocalTime,
  formatAmount,
  getBookingDayjs,
  getBookingStatusClass,
  getUserRole,
  hasAccess,
  logFieldConvertor
} from '../../helper/function'
import { defaultUserImg } from '../../helper/imagepath'
import { getAllActivityLog } from '../../redux/slices/logSlice'
import { SpinnerContext } from '../../context/spinnerContext'
import { getSchedulePropertyDataById } from '../../redux/slices/propertySlice'
import BadIcon from '../../assets/img/svg/bad-icon.svg'
import ShawerIcon from '../../assets/img/svg/shawer-icon.svg'

import StatusModal from '../modal/statusModal'
import CancelModalContent from '../scheduleStatus/canceleModalContent'
import ScheduledModalContent from '../scheduleStatus/scheduledModalContent'
import CommonStepper from '../steppers'

const ViewLogs = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { changeLoader } = useContext(SpinnerContext)
  const { allLogData } = useSelector((state) => state.log)
  const { scheduledProperties } = useSelector((state) => state.property)

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [modalName, setModalName] = useState(null)
  const [chatUserId, setChatUserId] = useState(null)

  const activityLogs = useMemo(() => allLogData?.activityLogs, [allLogData])
  const propertiesData = useMemo(() => allLogData?.propertiesData, [allLogData])

  const property_schedule_id = params?.id

  const clientName = useMemo(
    () =>
      scheduledProperties?.getPropertyScheduleData?.properties?.client
        ? scheduledProperties?.getPropertyScheduleData?.properties?.client?.first_name +
          ' ' +
          scheduledProperties?.getPropertyScheduleData?.properties?.client?.last_name
        : '',
    [scheduledProperties]
  )

  const overFun = useMemo(() => {
    return scheduledProperties?.getPropertyScheduleData?.next_checkin_time
      ? checkIfPastDate(scheduledProperties?.getPropertyScheduleData?.next_checkin_time, false)
      : checkIfPastDate(scheduledProperties?.getPropertyScheduleData?.booking_date_time, true)
  }, [scheduledProperties?.getPropertyScheduleData?.booking_date_time, scheduledProperties?.getPropertyScheduleData?.next_checkin_time])

  const getAllLogDetails = (id) => {
    changeLoader(true)
    dispatch(getAllActivityLog(id))
      .then(() => {
        changeLoader(false)
      })
      .catch(() => {
        changeLoader(false)
      })
  }

  useEffect(() => {
    getAllLogDetails(property_schedule_id)
  }, [property_schedule_id])

  const items = useMemo(() => {
    return activityLogs && activityLogs?.length !== 0
      ? [
          ...activityLogs.map((i) => {
            return {
              title: `${i.module}: ${i.operation}`,
              subTitle: (
                <div>
                  <p>{i.created_at ? convertTimestampToDate(i.created_at) : ''}</p>
                  <p>{i.created_at ? convertTimeToLocalTime(i.created_at) : ''}</p>
                </div>
              ),
              description: (
                <div className="view-log-list-main-div">
                  <div className="view-log-block">
                    <div className="view-log-row view-log-grid-row">
                      {Object.entries(JSON.parse(i.log)).map(([key, value]) => {
                        return (
                          <p key={key}>
                            {key} : {value ? <span>{logFieldConvertor(key, value, propertiesData?.properties?.timezone)}</span> : <span>--</span>}
                          </p>
                        )
                      })}
                    </div>
                  </div>
                </div>
              ),
              status: 'finish'
            }
          }),
          { title: '', subTitle: '', description: '', status: 'finish' }
        ]
      : []
  }, [activityLogs])

  const onHandleDetails = useCallback(
    (data) => {
      const { booking_status, property_schedule_id } = data
      setChatUserId(property_schedule_id)
      if (['scheduled', 'canceled', 'in progress', 'canceled cleaner']?.includes(booking_status)) {
        getPropertyData(property_schedule_id, booking_status)
      } else if (['completed']?.includes(booking_status)) {
        navigate(`/${`view-logs`}/complete-cleaning/${property_schedule_id}/cleaningDetail`)
      }
    },
    [navigate]
  )

  const getPropertyData = async (schedule_id, status) => {
    changeLoader(true)
    await dispatch(getSchedulePropertyDataById(schedule_id))
      .then(() => {
        setIsStatusModalOpen(true)
        setModalName(status)
        changeLoader(false)
      })
      .catch(() => {
        changeLoader(false)
      })
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>Cleaning #{propertiesData?.schedule_number} Changelog</h5>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-12 col-xl-12">
              <div className="common_card_div upcoming_cleanings_space_div custom-log-div">
                <div className="upcoming_cleanings_inner_box upcoming_cleanings_flex_div mb-2">
                  <div className="user_img_div">
                    <div className="cleanings_img_div">
                      <img
                        className="img-fluid "
                        src={propertiesData?.cleaners?.profile_image ? appendImageURL(propertiesData?.cleaners?.profile_image) : defaultUserImg}
                        alt="Image"
                      />
                    </div>
                    <div className="user_right_contains">
                      <h6 className="mb-0 user-name">
                        {propertiesData?.cleaners?.first_name} {propertiesData?.cleaners?.last_name}
                      </h6>
                    </div>
                  </div>

                  <div className="show_box show_bg" onClick={() => onHandleDetails(propertiesData)}>
                    <p>Details</p>
                  </div>
                </div>
                <div className="user_right_contains">
                  <h6 className="mb-2">{propertiesData?.cleaners?.address}</h6>
                </div>
                <div className="upcoming_cleanings_flex_div mb-2">
                  <p className="fs-14 gray_color fw-400">
                    {' '}
                    {`${propertiesData?.properties?.address} - ${propertiesData?.properties?.property_name}`}
                  </p>

                  <div className={`show_box ${getBookingStatusClass(propertiesData?.booking_status)}`}>
                    <p>{propertiesData?.booking_status}</p>
                  </div>
                </div>
                <div className="upcoming_cleanings_flex_div mb-2">
                  <div className="amount_div">
                    {hasAccess('accessFinalAmount', getUserRole()) && (
                      <h6 className="fs-14 black_color fw-400 me-3">{formatAmount(allLogData?.finalAmount) || 0}</h6>
                    )}
                    <label className="cleanings_icon_box_div">
                      <img className="img-fluid" src={ShawerIcon} alt="Image" />
                      {propertiesData?.properties?.number_of_bathroom}
                    </label>
                    <label className="cleanings_icon_box_div">
                      <img className="img-fluid" src={BadIcon} alt="Image" />
                      {propertiesData?.properties?.number_of_rooms}
                    </label>
                  </div>
                </div>
                <div className="upcoming_cleanings_flex_div">
                  <h6 className="fs-14 dark_blue_color fw-400">
                    <span>{getBookingDayjs(propertiesData, propertiesData?.properties?.timezone)?.format('DD MMM YYYY')}</span> |{' '}
                    <span>{getBookingDayjs(propertiesData, propertiesData?.properties?.timezone)?.format('hh:mm A')}</span>
                  </h6>
                </div>
              </div>
            </div>
          </div>

          <div className="common_card_div">
            <div className="row">
              <div className="col-md-12 col-lg-12 col-xl-12">
                <div className="stepper_div">
                  {items?.length !== 0 ? <CommonStepper items={items} /> : <div className="no-view-log">No Activity Log</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isStatusModalOpen && (
        <StatusModal
          onClose={() => setIsStatusModalOpen(false)}
          title={`Cleaning #${scheduledProperties?.getPropertyScheduleData?.schedule_number}`}
          headerContent={{
            name: clientName,
            status: scheduledProperties?.getPropertyScheduleData?.booking_status
          }}
          chatUserId={chatUserId}
          CheckTimeOver={overFun}
          clientId={scheduledProperties?.getPropertyScheduleData?.properties?.client?.client_id}
        >
          {['canceled', 'canceled cleaner']?.includes(modalName) && (
            <CancelModalContent
              data={scheduledProperties?.getPropertyScheduleData}
              finalAmount={scheduledProperties?.finalAmount}
              onClose={() => setIsStatusModalOpen(false)}
            />
          )}
          {['scheduled', 'in progress']?.includes(modalName) && (
            <ScheduledModalContent
              data={scheduledProperties?.getPropertyScheduleData}
              onClose={() => setIsStatusModalOpen(false)}
              page={`view-logs`}
            />
          )}
        </StatusModal>
      )}
    </>
  )
}

export default ViewLogs
